import type {
  InstanceScopedNamespacePath,
  InstanceScopedEntityType,
} from "building-types/components/instanceScopedNamespacePathTypes";
import type { Table } from "common/data/table";
import { arrayShallowEquals } from "common/utils/arrays";

export const ENTITY_ID_SEPARATOR = "__ci__";
export const INSTANCE_ID_SEPARATOR = "__";

export function composeComponentInstanceChain(chain: readonly string[]): string {
  return chain.join(INSTANCE_ID_SEPARATOR);
}

/** The archetype world, where no component instance applies and option choices cascade to archetypes. */
export const EMPTY_INSTANCE_CHAIN: readonly string[] = Object.freeze([]);

/** Key of the archetype world in tables keyed by `composeComponentInstanceChain`. */
export const ARCHETYPE_INSTANCE_CHAIN_KEY = composeComponentInstanceChain(EMPTY_INSTANCE_CHAIN);

export function instanceIdForComponent(entityId: string, chain?: readonly string[]): string {
  if (!chain) {
    return entityId;
  }

  return `${entityId}${ENTITY_ID_SEPARATOR}${composeComponentInstanceChain(chain)}`;
}

/** Archetype doc entity id from ids like `roof-a__ci__instance-a`, or the input id if not instance-scoped. */
export function archetypeEntityIdFromInstanceScopedId(instanceScopedId: string): string {
  const [archetypeId] = instanceScopedId.split(ENTITY_ID_SEPARATOR, 2);
  return archetypeId;
}

/** Instance chain from ids like `roof-a__ci__instance-a`, or undefined if not instance-scoped. */
export function componentInstanceChainFromId(instanceScopedId: string): readonly string[] | undefined {
  const [, componentInstanceChain] = instanceScopedId.split(ENTITY_ID_SEPARATOR, 2);
  if (componentInstanceChain === undefined) {
    return undefined;
  }
  return componentInstanceChain.split(INSTANCE_ID_SEPARATOR);
}

/**
 * Nested replication levels may appear both as the full path `[parent, …, leaf]` (from a parent
 * instance transform) and as a leaf-only path `[leaf]` (from the nested instance's own base
 * transform). Treat those as the same realized instance chain.
 */
export function componentInstanceChainsMatch(chain?: readonly string[], otherChain?: readonly string[]): boolean {
  if (arrayShallowEquals(chain, otherChain)) {
    return true;
  }
  if (!chain?.length || !otherChain?.length) {
    return false;
  }

  // Full nested path `[…, leaf]` is equivalent to a leaf-only path `[leaf]`.
  const chainLeaf = chain[chain.length - 1];
  const otherLeaf = otherChain[otherChain.length - 1];
  if (chain.length > 1 && arrayShallowEquals(otherChain, [chainLeaf])) {
    return true;
  }
  if (otherChain.length > 1 && arrayShallowEquals(chain, [otherLeaf])) {
    return true;
  }
  return false;
}

export function resolveInstanceScopedEntityIds<T extends InstanceScopedEntityType>(
  arr: readonly InstanceScopedNamespacePath<T>[],
): string[] {
  const levelIds: Set<string> = new Set(arr.map((m) => instanceIdForComponent(m.entityId, m.componentInstanceChain)));

  return Array.from(levelIds);
}

/**
 * Resolve the id of an instance scoped entity ref.
 * If the fallbackId is provided, the return type is enforced to be a string.
 * If the fallbackId is not provided, the return type could be a string or undefined.
 */
interface ResolveInstanceScopedEntityIdPropsBase<T extends InstanceScopedEntityType, Q> {
  readonly path: InstanceScopedNamespacePath<T> | undefined;
  readonly table: Table<Q>;
  readonly fallbackComponentInstanceChain?: string[];
}

interface ResolveInstanceScopedEntityIdPropsWithFallback<T extends InstanceScopedEntityType, Q>
  extends ResolveInstanceScopedEntityIdPropsBase<T, Q> {
  readonly fallbackId: string;
}

interface ResolveInstanceScopedEntityIdPropsWithoutFallback<T extends InstanceScopedEntityType, Q>
  extends ResolveInstanceScopedEntityIdPropsBase<T, Q> {
  readonly fallbackId?: undefined;
}

type ResolveInstanceScopedEntityIdProps<T extends InstanceScopedEntityType, Q> =
  | ResolveInstanceScopedEntityIdPropsWithFallback<T, Q>
  | ResolveInstanceScopedEntityIdPropsWithoutFallback<T, Q>;

export function resolveInstanceScopedEntityId<T extends InstanceScopedEntityType, Q>(
  props: ResolveInstanceScopedEntityIdPropsWithFallback<T, Q>,
): string;

export function resolveInstanceScopedEntityId<T extends InstanceScopedEntityType, Q>(
  props: ResolveInstanceScopedEntityIdPropsWithoutFallback<T, Q>,
): string | undefined;

export function resolveInstanceScopedEntityId<T extends InstanceScopedEntityType, Q>({
  path,
  table,
  fallbackId,
  fallbackComponentInstanceChain = [],
}: ResolveInstanceScopedEntityIdProps<T, Q>): string | undefined {
  const resolveEntityId = (): string | undefined => {
    if (!path) {
      return undefined;
    }

    const { entityId, componentInstanceChain = fallbackComponentInstanceChain } = path;

    if (componentInstanceChain.length === 0) {
      return entityId;
    }

    const compoundId = instanceIdForComponent(entityId, componentInstanceChain);
    if (!table[compoundId]) {
      return entityId;
    }

    return compoundId;
  };

  return resolveEntityId() ?? fallbackId;
}

/**
 * Resolve the entity of an instance scoped entity ref.
 * If the fallback is provided, the return type is enforced to be the type of the fallback.
 * If the fallback is not provided, the return type could be the type of the fallback or undefined.
 */
interface ResolveInstanceScopedEntityPropsBase<T extends InstanceScopedEntityType, Q> {
  readonly path: InstanceScopedNamespacePath<T> | undefined;
  readonly table: Table<Q>;
  readonly fallbackComponentInstanceChain?: string[];
}

interface ResolveInstanceScopedEntityPropsWithFallback<T extends InstanceScopedEntityType, Q>
  extends ResolveInstanceScopedEntityPropsBase<T, Q> {
  readonly fallback: Q;
}

interface ResolveInstanceScopedEntityPropsWithoutFallback<T extends InstanceScopedEntityType, Q>
  extends ResolveInstanceScopedEntityPropsBase<T, Q> {
  readonly fallback?: Q;
}

type ResolveInstanceScopedEntityProps<T extends InstanceScopedEntityType, Q> =
  | ResolveInstanceScopedEntityPropsWithFallback<T, Q>
  | ResolveInstanceScopedEntityPropsWithoutFallback<T, Q>;

export function resolveInstanceScopedEntity<T extends InstanceScopedEntityType, Q>(
  props: ResolveInstanceScopedEntityPropsWithFallback<T, Q>,
): Q;

export function resolveInstanceScopedEntity<T extends InstanceScopedEntityType, Q>(
  props: ResolveInstanceScopedEntityPropsWithoutFallback<T, Q>,
): Q | undefined;

export function resolveInstanceScopedEntity<T extends InstanceScopedEntityType, Q>({
  path,
  table,
  fallback,
  fallbackComponentInstanceChain,
}: ResolveInstanceScopedEntityProps<T, Q>): Q | undefined {
  const id = resolveInstanceScopedEntityId({ path, table, fallbackComponentInstanceChain });
  const entity = id ? table[id] : undefined;
  return entity ?? fallback;
}
